import {
  isGuaranteesApprovalLetterElectronicExecutionCheck,
  buildGuaranteesApplicationAmApprovalLetterClientSignedPath,
  buildGuaranteesApplicationAmApprovalLetterPath,
  buildGuaranteesApplicationAmApprovalLetterManualSignPath,
  buildGuaranteesApplicationInternalApprovalLetterPath,
  buildGuaranteesApplicationUdbtfGuaranteeScanCopyPath,
  buildGuaranteesApplicationUsapokApprovalPath,
  buildGuaranteesApplicationUakdoControllerControlPath,
  buildGuaranteesApplicationUakdoControlPath,
  buildGuaranteesApplicationPath,
  buildGuaranteesApplicationStep3Path,
} from './taskRouting.ts';
import type { TTaskApplication as TGuaranteesApplication } from './taskModels.ts';
import {
  AdministrationRole,
  GuaranteesApprovalStatus,
  GuaranteesApplicationStatus,
  type TTasksAssigneeUnit as TGuaranteesAssigneeUnit,
} from './taskModels.ts';
import { normalizeTaskApplicationStatus as normalizeGuaranteesApplicationStatus } from './taskModels.ts';
import {
  internalApprovalRoles,
  internalApprovalRoleToType,
  internalApprovalStatusLabels,
  internalApprovalTypeLabels,
  type TInternalApprovalRole,
} from './internalApprovalConstants.ts';
import { taskRoleLabels, taskServiceOrder, taskServicesConfig } from './taskConfig.ts';
import type {
  ITaskRow,
  TTaskInternalApprovalType,
  TTaskRoleKey,
  TTaskServiceKey,
  TTaskUrgencyKey,
} from './types.ts';

export const getTaskSectionMetaText = (rowsCount: number, emptyText: string): string =>
  rowsCount ? `${rowsCount} активних завдань` : emptyText;

export const getTaskSortTooltipTitle = (active: boolean, direction: string): string =>
  active ? `РЎРѕСЂС‚СѓРІР°РЅРЅСЏ: ${direction}` : 'РЎРѕСЂС‚СѓРІР°С‚Рё';

export const formatTaskDate = (value?: string): string => {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return `${date.toLocaleDateString('uk-UA')} ${date.toLocaleTimeString('uk-UA', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })}`;
};

export const guaranteesTaskInProgressStatusByAwaitingStatus: Partial<
  Record<GuaranteesApplicationStatus, GuaranteesApplicationStatus>
> = {
  [GuaranteesApplicationStatus.AWAITING_CLIENT_INITIAL_SIGN]:
    GuaranteesApplicationStatus.IN_PROGRESS_CLIENT_INITIAL_SIGN,
  [GuaranteesApplicationStatus.AWAITING_AM_APPROVAL_LETTER]:
    GuaranteesApplicationStatus.IN_PROGRESS_AM_APPROVAL_LETTER,
  [GuaranteesApplicationStatus.AWAITING_PM_MANAGER_TERMS]:
    GuaranteesApplicationStatus.IN_PROGRESS_PM_MANAGER_TERMS,
  [GuaranteesApplicationStatus.AWAITING_UDBTF_ISSUANCE_TERMS]:
    GuaranteesApplicationStatus.IN_PROGRESS_UDBTF_ISSUANCE_TERMS,
  [GuaranteesApplicationStatus.AWAITING_INTERNAL_APPROVALS]:
    GuaranteesApplicationStatus.IN_PROGRESS_INTERNAL_APPROVALS,
  [GuaranteesApplicationStatus.AWAITING_CLIENT_APPROVAL_LETTER_SIGN]:
    GuaranteesApplicationStatus.IN_PROGRESS_CLIENT_APPROVAL_LETTER_SIGN,
  [GuaranteesApplicationStatus.AWAITING_AM_APPROVAL_LETTER_SENT_MANUAL_SIGN]:
    GuaranteesApplicationStatus.IN_PROGRESS_AM_APPROVAL_LETTER_SENT_MANUAL_SIGN,
  [GuaranteesApplicationStatus.AWAITING_UAKDO_APPROVAL]:
    GuaranteesApplicationStatus.IN_PROGRESS_UAKDO_APPROVAL,
  [GuaranteesApplicationStatus.AWAITING_UAKDO_CONTROLLER_APPROVAL]:
    GuaranteesApplicationStatus.IN_PROGRESS_UAKDO_CONTROLLER_APPROVAL,
  [GuaranteesApplicationStatus.AWAITING_UDBTF_APPROVAL]:
    GuaranteesApplicationStatus.IN_PROGRESS_UDBTF_APPROVAL,
  [GuaranteesApplicationStatus.AWAITING_GUARANTEE_ISSUANCE]:
    GuaranteesApplicationStatus.IN_PROGRESS_GUARANTEE_ISSUANCE,
  [GuaranteesApplicationStatus.AWAITING_USAPOK_APPROVAL]:
    GuaranteesApplicationStatus.IN_PROGRESS_USAPOK_APPROVAL,
  [GuaranteesApplicationStatus.AWAITING_USAPOK_CONTROLLER_APPROVAL]:
    GuaranteesApplicationStatus.IN_PROGRESS_USAPOK_CONTROLLER_APPROVAL,
};

export const getGuaranteesTaskInProgressStatus = (
  status?: GuaranteesApplicationStatus | string
): GuaranteesApplicationStatus | undefined =>
  guaranteesTaskInProgressStatusByAwaitingStatus[normalizeGuaranteesApplicationStatus(status)];

const guaranteesTaskInProgressStatuses = new Set<GuaranteesApplicationStatus>(
  Object.values(guaranteesTaskInProgressStatusByAwaitingStatus).filter(
    (status): status is GuaranteesApplicationStatus => !!status
  )
);

export const isGuaranteesTaskInProgressStatus = (
  status?: GuaranteesApplicationStatus | string | null
): boolean => guaranteesTaskInProgressStatuses.has(normalizeGuaranteesApplicationStatus(status));

export const guaranteesTaskAwaitingStatusByInProgressStatus: Partial<
  Record<GuaranteesApplicationStatus, GuaranteesApplicationStatus>
> = Object.fromEntries(
  Object.entries(guaranteesTaskInProgressStatusByAwaitingStatus).map(
    ([awaitingStatus, inProgressStatus]) => [inProgressStatus, awaitingStatus]
  )
) as Partial<Record<GuaranteesApplicationStatus, GuaranteesApplicationStatus>>;

export const getGuaranteesTaskAwaitingStatus = (
  status?: GuaranteesApplicationStatus | string
): GuaranteesApplicationStatus | undefined =>
  guaranteesTaskAwaitingStatusByInProgressStatus[normalizeGuaranteesApplicationStatus(status)];

const guaranteesUdbtfTaskStatuses = new Set<GuaranteesApplicationStatus>([
  GuaranteesApplicationStatus.AWAITING_UDBTF_ISSUANCE_TERMS,
  GuaranteesApplicationStatus.IN_PROGRESS_UDBTF_ISSUANCE_TERMS,
]);

const guaranteesInternalApprovalTaskStatuses = new Set<GuaranteesApplicationStatus>([
  GuaranteesApplicationStatus.AWAITING_INTERNAL_APPROVALS,
  GuaranteesApplicationStatus.IN_PROGRESS_INTERNAL_APPROVALS,
]);

const guaranteesAmApprovalLetterTaskStatuses = new Set<GuaranteesApplicationStatus>([
  GuaranteesApplicationStatus.AWAITING_AM_APPROVAL_LETTER,
  GuaranteesApplicationStatus.IN_PROGRESS_AM_APPROVAL_LETTER,
]);

const guaranteesAmApprovalLetterManualSignTaskStatuses = new Set<GuaranteesApplicationStatus>([
  GuaranteesApplicationStatus.AWAITING_AM_APPROVAL_LETTER_SENT_MANUAL_SIGN,
  GuaranteesApplicationStatus.IN_PROGRESS_AM_APPROVAL_LETTER_SENT_MANUAL_SIGN,
]);

const guaranteesUakdoControlTaskStatuses = new Set<GuaranteesApplicationStatus>([
  GuaranteesApplicationStatus.AWAITING_UAKDO_APPROVAL,
  GuaranteesApplicationStatus.IN_PROGRESS_UAKDO_APPROVAL,
]);

const guaranteesUakdoControllerControlTaskStatuses = new Set<GuaranteesApplicationStatus>([
  GuaranteesApplicationStatus.AWAITING_UAKDO_CONTROLLER_APPROVAL,
  GuaranteesApplicationStatus.IN_PROGRESS_UAKDO_CONTROLLER_APPROVAL,
]);

const guaranteesUdbtfGuaranteeScanCopyTaskStatuses = new Set<GuaranteesApplicationStatus>([
  GuaranteesApplicationStatus.AWAITING_UDBTF_APPROVAL,
  GuaranteesApplicationStatus.IN_PROGRESS_UDBTF_APPROVAL,
]);

const guaranteesUsapokApprovalTaskStatuses = new Set<GuaranteesApplicationStatus>([
  GuaranteesApplicationStatus.AWAITING_USAPOK_APPROVAL,
  GuaranteesApplicationStatus.IN_PROGRESS_USAPOK_APPROVAL,
]);

export const isInternalApprovalRole = (role: TTaskRoleKey | ''): role is TInternalApprovalRole =>
  !!role && internalApprovalRoles.includes(role);

export const normalizeSelectedTaskRoles = (
  selectedRoles: readonly TTaskRoleKey[],
  availableRoles?: readonly TTaskRoleKey[]
): TTaskRoleKey[] => {
  const [selectedRole] = selectedRoles;

  if (!selectedRole) {
    return [];
  }

  if (availableRoles && !availableRoles.includes(selectedRole)) {
    return [];
  }

  return [selectedRole];
};

export const buildDefaultSelectedTaskRoles = (
  availableRoles: readonly TTaskRoleKey[]
): TTaskRoleKey[] => availableRoles.slice(0, 1);

const isInternalApprovalType = (value: string): value is TTaskInternalApprovalType =>
  value === 'udbtf' || value === 'uo' || value === 'umvn';

const getTaskAdministrationRole = (application?: TGuaranteesApplication): TTaskRoleKey | '' => {
  if (!application) {
    return '';
  }

  const record = application as Record<string, unknown>;
  const role = getTaskDisplayValue(
    record.administrationRole ||
      record.approvalRole ||
      record.adminRole ||
      record.taskRole ||
      record.executorRole ||
      record.role
  );

  return role as TTaskRoleKey | '';
};

const getTaskInternalApprovalType = (
  application?: TGuaranteesApplication
): TTaskInternalApprovalType | '' => {
  if (!application) {
    return '';
  }

  const record = application as Record<string, unknown>;
  const approvalType = getTaskDisplayValue(
    record.approvalType || record.internalApprovalType || record.approvalLetterType
  ).toLowerCase();

  return isInternalApprovalType(approvalType) ? approvalType : '';
};

const getSingleSelectedInternalApprovalRole = (
  selectedRoles: readonly TTaskRoleKey[] = []
): TInternalApprovalRole | '' => {
  const [internalRole] = selectedRoles;

  return internalRole && isInternalApprovalRole(internalRole) ? internalRole : '';
};

const getInternalApprovalTypeForTask = (
  status?: GuaranteesApplicationStatus | string | null,
  context: {
    application?: TGuaranteesApplication;
    selectedRoles?: readonly TTaskRoleKey[];
  } = {}
): TTaskInternalApprovalType | '' => {
  const normalizedStatus = normalizeGuaranteesApplicationStatus(status);

  if (!guaranteesInternalApprovalTaskStatuses.has(normalizedStatus)) {
    return '';
  }

  const taskApprovalType = getTaskInternalApprovalType(context.application);

  if (taskApprovalType) {
    return taskApprovalType;
  }

  const taskRole = getTaskAdministrationRole(context.application);

  if (isInternalApprovalRole(taskRole)) {
    return internalApprovalRoleToType[taskRole];
  }

  const selectedInternalRole = getSingleSelectedInternalApprovalRole(context.selectedRoles);

  return selectedInternalRole ? internalApprovalRoleToType[selectedInternalRole] : '';
};

const getInternalApprovalStatusForTask = (
  application: TGuaranteesApplication,
  internalApprovalType: TTaskInternalApprovalType | ''
): string => {
  if (internalApprovalType === 'udbtf') {
    return getTaskDisplayValue(application.udbtfApprovalStatus);
  }

  if (internalApprovalType === 'uo') {
    return getTaskDisplayValue(application.uoApprovalStatus);
  }

  if (internalApprovalType === 'umvn') {
    return getTaskDisplayValue(application.umvnApprovalStatus);
  }

  return '';
};

export const getGuaranteesTaskAssigneeUnit = (
  status?: GuaranteesApplicationStatus | string | null,
  internalApprovalType?: TTaskInternalApprovalType | ''
): TGuaranteesAssigneeUnit => {
  const normalizedStatus = normalizeGuaranteesApplicationStatus(status);

  if (normalizedStatus !== GuaranteesApplicationStatus.AWAITING_INTERNAL_APPROVALS) {
    return 'DEFAULT';
  }

  if (internalApprovalType === 'udbtf') {
    return 'UDBTF';
  }

  if (internalApprovalType === 'uo') {
    return 'UO';
  }

  if (internalApprovalType === 'umvn') {
    return 'UMVN';
  }

  return 'DEFAULT';
};

export const getGuaranteesTaskAssigneeEmail = (
  application: TGuaranteesApplication,
  status?: GuaranteesApplicationStatus | string | null,
  internalApprovalType?: TTaskInternalApprovalType | ''
): string => {
  const normalizedStatus = normalizeGuaranteesApplicationStatus(status);

  if (normalizedStatus !== GuaranteesApplicationStatus.AWAITING_INTERNAL_APPROVALS) {
    return getTaskDisplayValue(application.assignedEmployeeEmail);
  }

  if (internalApprovalType === 'udbtf') {
    return getTaskDisplayValue(application.udbtfAssignedEmployeeEmail);
  }

  if (internalApprovalType === 'uo') {
    return getTaskDisplayValue(application.uoAssignedEmployeeEmail);
  }

  if (internalApprovalType === 'umvn') {
    return getTaskDisplayValue(application.umvnAssignedEmployeeEmail);
  }

  return '';
};

const isInternalApprovalInProgress = (approvalStatus?: string | null): boolean =>
  getTaskDisplayValue(approvalStatus) === GuaranteesApprovalStatus.IN_PROGRESS;

const getCanViewGuaranteesTaskApplication = ({
  guaranteeStatus,
  internalApprovalStatus,
  internalApprovalType,
  isAssignedToCurrentUser,
}: {
  guaranteeStatus?: GuaranteesApplicationStatus | string | null;
  internalApprovalStatus?: string | null;
  internalApprovalType?: TTaskInternalApprovalType | '';
  isAssignedToCurrentUser: boolean;
}): boolean => {
  if (!isAssignedToCurrentUser) {
    return false;
  }

  if (internalApprovalType) {
    return isInternalApprovalInProgress(internalApprovalStatus);
  }

  return isGuaranteesTaskInProgressStatus(guaranteeStatus);
};

const buildInternalApprovalStatusTooltipDescription = (
  application: TGuaranteesApplication,
  internalApprovalType: TTaskInternalApprovalType | ''
): string | undefined => {
  if (!internalApprovalType) {
    return undefined;
  }

  const approvalStatus = getInternalApprovalStatusForTask(application, internalApprovalType);

  if (!approvalStatus) {
    return undefined;
  }

  const approvalStatusLabel = internalApprovalStatusLabels[approvalStatus] ?? approvalStatus;

  return `Статус ${internalApprovalTypeLabels[internalApprovalType]}: ${approvalStatusLabel}`;
};

export const isGuaranteesUdbtfIssuanceTermsTaskStatus = (
  status?: GuaranteesApplicationStatus | string | null
): boolean => guaranteesUdbtfTaskStatuses.has(normalizeGuaranteesApplicationStatus(status));

export const isGuaranteesAmApprovalLetterTaskStatus = (
  status?: GuaranteesApplicationStatus | string | null
): boolean =>
  guaranteesAmApprovalLetterTaskStatuses.has(normalizeGuaranteesApplicationStatus(status));

export const isGuaranteesAmApprovalLetterManualSignTaskStatus = (
  status?: GuaranteesApplicationStatus | string | null
): boolean =>
  guaranteesAmApprovalLetterManualSignTaskStatuses.has(
    normalizeGuaranteesApplicationStatus(status)
  );

export const isGuaranteesUakdoControlTaskStatus = (
  status?: GuaranteesApplicationStatus | string | null
): boolean => guaranteesUakdoControlTaskStatuses.has(normalizeGuaranteesApplicationStatus(status));

export const isGuaranteesUakdoControllerControlTaskStatus = (
  status?: GuaranteesApplicationStatus | string | null
): boolean =>
  guaranteesUakdoControllerControlTaskStatuses.has(normalizeGuaranteesApplicationStatus(status));

export const isGuaranteesUdbtfGuaranteeScanCopyTaskStatus = (
  status?: GuaranteesApplicationStatus | string | null
): boolean =>
  guaranteesUdbtfGuaranteeScanCopyTaskStatuses.has(normalizeGuaranteesApplicationStatus(status));

export const isGuaranteesUsapokApprovalTaskStatus = (
  status?: GuaranteesApplicationStatus | string | null
): boolean =>
  guaranteesUsapokApprovalTaskStatuses.has(normalizeGuaranteesApplicationStatus(status));

export const getVisibleTaskServices = (
  selectedRoles: readonly TTaskRoleKey[]
): TTaskServiceKey[] => {
  const selectedRoleSet = new Set(selectedRoles);

  return taskServiceOrder.filter((serviceKey) =>
    taskServicesConfig[serviceKey].roles.some((role) => selectedRoleSet.has(role))
  );
};

export const buildAvailableGuaranteesTaskRoles = (
  administrationRoles: readonly TTaskRoleKey[],
  access: {
    hasUakdoResponsibleAccess: boolean;
    hasUakdoControllerAccess: boolean;
  }
): TTaskRoleKey[] => {
  const roles = new Set<TTaskRoleKey>(administrationRoles);

  if (access.hasUakdoResponsibleAccess) {
    roles.add(AdministrationRole.UAKDO_ADMINISTRATOR);
  }

  if (access.hasUakdoControllerAccess) {
    roles.add(AdministrationRole.UAKDO_CONTROLLER);
  }

  return Array.from(roles);
};

export const getTaskDisplayValue = (value: unknown): string => String(value ?? '').trim();

export const normalizeEmail = (value?: string | null): string => String(value ?? '').trim();

const normalizeEmailForComparison = (value?: string | null): string =>
  normalizeEmail(value).toLowerCase();

export const isSameEmail = (leftEmail?: string | null, rightEmail?: string | null): boolean => {
  const left = normalizeEmailForComparison(leftEmail);
  const right = normalizeEmailForComparison(rightEmail);

  return !!left && !!right && left === right;
};

const guaranteesControllerRoles = new Set<TTaskRoleKey>([
  AdministrationRole.MANAGER_CONTROLLER,
  AdministrationRole.UAKDO_CONTROLLER,
  AdministrationRole.USAPOK_CONTROLLER,
  AdministrationRole.UBTF_CONTROLLER,
  AdministrationRole.UO_CONTROLLER,
  AdministrationRole.UMVN_CONTROLLER,
]);

export const isGuaranteesControllerRole = (role: TTaskRoleKey): boolean =>
  guaranteesControllerRoles.has(role);

const isValidTaskSlaDeadline = (deadline?: string | null): deadline is string => {
  if (!deadline) {
    return false;
  }

  return Number.isFinite(new Date(deadline).getTime());
};

export const getGuaranteesTaskSla = (
  application: TGuaranteesApplication,
  internalApprovalType: TTaskInternalApprovalType | ''
): Pick<ITaskRow, 'slaDeadline' | 'slaOverdue'> => {
  const sla =
    internalApprovalType === 'udbtf'
      ? {
          deadline: application.udbtfProcessingDeadline,
          overdue: application.udbtfProcessingOverdue,
        }
      : internalApprovalType === 'umvn'
        ? {
            deadline: application.umvnProcessingDeadline,
            overdue: application.umvnProcessingOverdue,
          }
        : internalApprovalType === 'uo'
          ? {
              deadline: application.uoProcessingDeadline,
              overdue: application.uoProcessingOverdue,
            }
          : {
              deadline: application.processingDeadline,
              overdue: application.processingOverdue,
            };

  return isValidTaskSlaDeadline(sla.deadline)
    ? {
        slaDeadline: sla.deadline,
        slaOverdue: sla.overdue === true,
      }
    : {};
};

const hasExplicitApprovalLetterExecutionCheck = (application: TGuaranteesApplication): boolean =>
  !!getTaskDisplayValue(
    application.executionChecks?.applicationForm ||
      application.executionChecks?.executionCheck ||
      application.executionCheck
  );

const getBooleanRecordValue = (
  record: Record<string, unknown> | undefined,
  keys: readonly string[]
): boolean | undefined => {
  for (const key of keys) {
    const value = record?.[key];

    if (typeof value === 'boolean') {
      return value;
    }
  }

  return undefined;
};

export const getGuaranteesTaskIsUrgent = (application: TGuaranteesApplication): boolean =>
  getBooleanRecordValue(application.root, ['isUrgent']) ??
  getBooleanRecordValue(application, ['isUrgent']) ??
  getBooleanRecordValue(application.root, ['is_urgent', 'urgent']) ??
  false;

export const mapGuaranteesManagerTasksToTaskRows = (
  managerTasks?: TGuaranteesApplication[] | null,
  options: {
    currentUserEmail?: string | null;
    selectedRoles?: readonly TTaskRoleKey[];
  } = {}
): ITaskRow[] =>
  (managerTasks ?? []).map((application) => {
    const contragentId = getTaskDisplayValue(application.contragentId);
    const applicationId = getTaskDisplayValue(application.id || application.applicationId);
    const principal = application.principal;
    const root = application.root;
    const counterparty =
      getTaskDisplayValue(principal?.shortName) ||
      getTaskDisplayValue(principal?.fullName) ||
      contragentId;
    const applicationNumber =
      getTaskDisplayValue(root?.applicationNumber) ||
      getTaskDisplayValue(application.applicationNumber) ||
      applicationId;
    const date =
      getTaskDisplayValue(application.createdAt) || getTaskDisplayValue(root?.applicationDate);

    const guaranteeStatus =
      getTaskDisplayValue(application.applicationStatus) || GuaranteesApplicationStatus.DRAFT;
    const normalizedGuaranteeStatus = normalizeGuaranteesApplicationStatus(guaranteeStatus);
    const internalApprovalType = getInternalApprovalTypeForTask(normalizedGuaranteeStatus, {
      application,
      selectedRoles: options.selectedRoles,
    });
    const guaranteeStatusTooltipDescription = buildInternalApprovalStatusTooltipDescription(
      application,
      internalApprovalType
    );
    const assigneeUnit = getGuaranteesTaskAssigneeUnit(
      normalizedGuaranteeStatus,
      internalApprovalType
    );
    const assigneeEmail = getGuaranteesTaskAssigneeEmail(
      application,
      normalizedGuaranteeStatus,
      internalApprovalType
    );
    const internalApprovalStatus = getInternalApprovalStatusForTask(
      application,
      internalApprovalType
    );
    const isAssignedToCurrentUser = isSameEmail(assigneeEmail, options.currentUserEmail);
    const sla = getGuaranteesTaskSla(application, internalApprovalType);
    const hasExplicitExecutionCheck = hasExplicitApprovalLetterExecutionCheck(application);
    const actionPath = isGuaranteesAmApprovalLetterManualSignTaskStatus(normalizedGuaranteeStatus)
      ? hasExplicitExecutionCheck && isGuaranteesApprovalLetterElectronicExecutionCheck(application)
        ? buildGuaranteesApplicationAmApprovalLetterClientSignedPath(applicationId, contragentId)
        : buildGuaranteesApplicationAmApprovalLetterManualSignPath(applicationId, contragentId)
      : isGuaranteesAmApprovalLetterTaskStatus(normalizedGuaranteeStatus)
        ? buildGuaranteesApplicationAmApprovalLetterPath(applicationId, contragentId, true)
        : isGuaranteesUakdoControlTaskStatus(normalizedGuaranteeStatus)
          ? buildGuaranteesApplicationUakdoControlPath(applicationId, contragentId)
          : isGuaranteesUakdoControllerControlTaskStatus(normalizedGuaranteeStatus)
            ? buildGuaranteesApplicationUakdoControllerControlPath(applicationId, contragentId)
            : isGuaranteesUdbtfGuaranteeScanCopyTaskStatus(normalizedGuaranteeStatus)
              ? buildGuaranteesApplicationUdbtfGuaranteeScanCopyPath(applicationId, contragentId)
              : isGuaranteesUsapokApprovalTaskStatus(normalizedGuaranteeStatus)
                ? buildGuaranteesApplicationUsapokApprovalPath(applicationId, contragentId)
                : internalApprovalType
                  ? buildGuaranteesApplicationInternalApprovalLetterPath(
                      applicationId,
                      contragentId,
                      internalApprovalType
                    )
                  : isGuaranteesUdbtfIssuanceTermsTaskStatus(normalizedGuaranteeStatus)
                    ? buildGuaranteesApplicationStep3Path(applicationId, contragentId, true)
                    : buildGuaranteesApplicationPath(applicationId, contragentId, true);

    return {
      id: applicationId,
      reference: applicationNumber,
      subject: counterparty,
      task: applicationNumber,
      deadline: '',
      status: 'new',
      counterparty,
      counterpartyCode: getTaskDisplayValue(principal?.identifyCode) || contragentId,
      date,
      executor:
        getTaskDisplayValue(application.managerEmail) || getTaskDisplayValue(application.managerId),
      assigneeEmail,
      assigneeUnit,
      isUrgent: getGuaranteesTaskIsUrgent(application),
      isAssignedToCurrentUser,
      canViewApplication: getCanViewGuaranteesTaskApplication({
        guaranteeStatus: normalizedGuaranteeStatus,
        internalApprovalStatus,
        internalApprovalType,
        isAssignedToCurrentUser,
      }),
      guaranteeStatus,
      guaranteeStatusTooltipDescription,
      internalApprovalStatus,
      internalApprovalType: internalApprovalType || undefined,
      ...sla,
      version: application.version,
      actionPath,
    };
  });

export const getTaskRoleLabel = (role: TTaskRoleKey): string => taskRoleLabels[role] ?? role;

const TASK_SOON_THRESHOLD_DAYS = 2;
const DATE_PARTS_COUNT = 3;

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const parseTaskDeadline = (deadline: string): Date | null => {
  const [day, month, year] = deadline.split('.').map(Number);

  if (!day || !month || !year || deadline.split('.').length !== DATE_PARTS_COUNT) {
    return null;
  }

  const parsedDate = new Date(year, month - 1, day);

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

export const getTaskDeadlineUrgency = (
  deadline: string,
  currentDate = new Date()
): TTaskUrgencyKey => {
  const parsedDeadline = parseTaskDeadline(deadline);

  if (!parsedDeadline) {
    return 'later';
  }

  const millisecondsInDay = 24 * 60 * 60 * 1000;
  const daysUntilDeadline = Math.ceil(
    (startOfDay(parsedDeadline).getTime() - startOfDay(currentDate).getTime()) / millisecondsInDay
  );

  if (daysUntilDeadline <= 0) {
    return 'now';
  }

  return daysUntilDeadline <= TASK_SOON_THRESHOLD_DAYS ? 'soon' : 'later';
};

export const hasImmediateTasks = (rows: readonly ITaskRow[], currentDate = new Date()): boolean =>
  rows.some(({ deadline }) => getTaskDeadlineUrgency(deadline, currentDate) === 'now');
